import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import fetchData from '../../functions/fetchData';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import './MonthlySales.css';

function MonthlySales() {
  const [monthlySalesData, setMonthlySalesData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalTransactions: 0,
    averagePerMonth: 0,
    bestMonth: '',
    bestMonthSales: 0,
    growth: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchMonthlySalesData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch bills data directly from the bills table only
      const billsData = await fetchData('bills', null, 'bill_date', {});
      
      if (billsData && Array.isArray(billsData)) {
        // Filter bills for the selected year and active status
        const yearBills = billsData.filter(bill => {
          const billYear = new Date(bill.bill_date).getFullYear();
          return billYear === selectedYear && bill.status === 'active';
        });
        
        // Process monthly sales data
        const monthlyData = {};
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        // Initialize all 12 months
        for (let i = 0; i < 12; i++) {
          monthlyData[monthNames[i]] = {
            month: monthNames[i],
            sales: 0,
            transactions: 0
          };
        }
        
        // Process bills data
        yearBills.forEach(bill => {
          const billDate = new Date(bill.bill_date);
          const monthIndex = billDate.getMonth();
          const monthName = monthNames[monthIndex];
          const amount = parseFloat(bill.total_amount) || 0;
          
          monthlyData[monthName].sales += amount;
          monthlyData[monthName].transactions += 1;
        });
        
        // Convert to array
        const monthlySalesArray = Object.values(monthlyData);
        setMonthlySalesData(monthlySalesArray);
        
        // Calculate summary statistics
        const totalSales = yearBills.reduce((sum, bill) => sum + (parseFloat(bill.total_amount) || 0), 0);
        const totalTransactions = yearBills.length;
        const averagePerMonth = monthlySalesArray.length > 0 ? totalSales / 12 : 0;
        
        // Find best month
        const bestMonthData = monthlySalesArray.reduce((max, month) => 
          (month.sales || 0) > (max.sales || 0) ? month : max, monthlySalesArray[0] || {});
        
        // Calculate year-over-year growth (compare with previous year)
        const lastYearBills = billsData.filter(bill => {
          const billYear = new Date(bill.bill_date).getFullYear();
          return billYear === (selectedYear - 1) && bill.status === 'active';
        });
        const lastYearTotal = lastYearBills.reduce((sum, bill) => sum + (parseFloat(bill.total_amount) || 0), 0);
        const growth = lastYearTotal > 0 ? ((totalSales - lastYearTotal) / lastYearTotal * 100) : 0;
        
        setSummaryData({
          totalSales,
          totalTransactions,
          averagePerMonth,
          bestMonth: bestMonthData.month || '',
          bestMonthSales: bestMonthData.sales || 0,
          growth
        });
      } else {
        // Handle case when no monthly data is returned
        setMonthlySalesData([]);
        setSummaryData({
          totalSales: 0,
          totalTransactions: 0,
          averagePerMonth: 0,
          bestMonth: '',
          bestMonthSales: 0,
          growth: 0
        });
      }
    } catch (error) {
      console.error('Error fetching monthly sales data:', error);
      setError('Failed to load monthly sales data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlySalesData();
  }, [selectedYear]);

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  };

  // Multi-color palette for monthly bar chart
  const MONTHLY_COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#f1c40f', '#8e44ad', '#16a085'];

  if (isLoading) {
    return (
      <Layout>
        <Header title="Monthly Sales Report" />
        <div className="container-fluid">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading monthly sales report...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Monthly Sales Report" />
      <div className="monthly-sales-container">
        
        {/* Controls */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="yearSelect">Select Year:</label>
              <select
                id="yearSelect"
                className="form-control"
                value={selectedYear}
                onChange={handleYearChange}
              >
                {generateYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6 text-end">
            <button 
              className="btn btn-outline-primary"
              onClick={fetchMonthlySalesData}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card total-sales">
              <div className="card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.totalSales.toLocaleString()}</h3>
                <p>Total Yearly Sales</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card total-transactions">
              <div className="card-icon">
                <i className="fas fa-receipt"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalTransactions.toLocaleString()}</h3>
                <p>Total Transactions</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card average-sales">
              <div className="card-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.averagePerMonth.toLocaleString()}</h3>
                <p>Average Per Month</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card best-month">
              <div className="card-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.bestMonth}</h3>
                <p>Best Month (฿{summaryData.bestMonthSales.toLocaleString()})</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card growth">
              <div className="card-icon">
                <i className={`fas ${summaryData.growth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
              </div>
              <div className="card-info">
                <h3 className={summaryData.growth >= 0 ? 'text-success' : 'text-danger'}>
                  {summaryData.growth >= 0 ? '+' : ''}{summaryData.growth.toFixed(1)}%
                </h3>
                <p>YoY Growth</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row">
          {/* Monthly Bar Chart */}
          <div className="col-lg-8 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Monthly Sales Trend - {selectedYear}</h5>
              </div>
              <div className="panel-content">
                {monthlySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={monthlySalesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [
                          `฿${value.toLocaleString()}`,
                          name === 'sales' ? 'Sales' : 'Transactions'
                        ]}
                      />
                      <Bar dataKey="sales" name="sales">
                        {monthlySalesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={MONTHLY_COLORS[index % MONTHLY_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-bar"></i>
                    <p>No sales data available for {selectedYear}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Yearly Comparison - Temporarily Disabled */}
          <div className="col-lg-4 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Year Comparison</h5>
              </div>
              <div className="panel-content">
                <div className="no-data">
                  <i className="fas fa-chart-line"></i>
                  <p>Yearly comparison feature is temporarily unavailable</p>
                  <small className="text-muted">This feature will be available when the yearly comparison API is implemented</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Detailed Monthly Report - {selectedYear}</h5>
              </div>
              <div className="panel-content">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Sales Amount</th>
                        <th>Transactions</th>
                        <th>Avg per Transaction</th>
                        <th>Growth from Prev Month</th>
                        <th>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlySalesData.length > 0 ? (
                        monthlySalesData.map((month, index) => {
                          const prevMonthSales = index > 0 ? monthlySalesData[index - 1].sales : month.sales;
                          const growth = prevMonthSales > 0 ? ((month.sales - prevMonthSales) / prevMonthSales * 100) : 0;
                          const avgPerTransaction = month.transactions > 0 ? month.sales / month.transactions : 0;
                          const percentOfTotal = summaryData.totalSales > 0 ? (month.sales / summaryData.totalSales * 100) : 0;
                          
                          return (
                            <tr key={index}>
                              <td className="font-weight-bold">{month.month}</td>
                              <td className="text-success">฿{month.sales.toLocaleString()}</td>
                              <td>{month.transactions}</td>
                              <td>฿{avgPerTransaction.toFixed(2)}</td>
                              <td className={index === 0 ? 'text-muted' : (growth >= 0 ? 'text-success' : 'text-danger')}>
                                {index === 0 ? '-' : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}
                              </td>
                              <td>{percentOfTotal.toFixed(1)}%</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            No data available for {selectedYear}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default MonthlySales;
